/**
 * HTTP 请求配置接口
 */
interface IHttpRequestConfig {
  url: string
  method?: 'GET' | 'POST'
  data?: any
  params?: Record<string, any>
  headers?: Record<string, string>
  baseUrl?: string
}

/**
 * HTTP 响应接口
 */
interface IHttpResponse<T = any> {
  code: number | string
  data: T
  message?: string
}

/**
 * Session 数据接口
 */
interface ISessionData {
  token: string
}

/**
 * 通用 HTTP 请求封装
 * 使用 XMLHttpRequest 实现，支持 GET 和 POST 方法
 * 自动使用 Session.getToken 获取认证信息
 * @param {IHttpRequestConfig} config 请求配置
 * @return {Promise<T>} 返回 Promise 包装的响应数据
 */
function httpRequest<T = any>(config: IHttpRequestConfig): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    // 使用 Session.getToken 包装请求以获取认证信息
    (window as any).Session.getToken((sessionData: ISessionData) => {
      const xhr = new XMLHttpRequest()
      const { url, method = 'GET', data, params, headers = {}, baseUrl } = config
      const token = sessionData.token

      // 构建完整的请求 URL
      let fullUrl = url
      if (baseUrl) {
        fullUrl = `${baseUrl}${url}`
      }

      // 构建查询参数字符串
      const buildQueryString = (queryParams: Record<string, any>): string => {
        return Object.keys(queryParams)
          .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
          .join('&')
      }

      const useHeader = !!(window as any).__WK_SESSION_TOKEN_USE_HEADER__

      // 对于 GET 请求，将参数添加到查询参数中（header 模式下不注入 token）
      if (method === 'GET') {
        const queryParams = useHeader ? { ...params } : { token, ...params }
        const queryString = buildQueryString(queryParams)
        if (queryString) {
          const separator = fullUrl.indexOf('?') !== -1 ? '&' : '?'
          fullUrl = `${fullUrl}${separator}${queryString}`
        }
      }

      xhr.open(method, fullUrl, true)

      // 设置请求头
      if (useHeader) {
        xhr.setRequestHeader('Authorization', 'Bearer ' + token)
      }
      Object.keys(headers).forEach((key) => {
        xhr.setRequestHeader(key, headers[key])
      })

      // 对于 POST 请求，设置 Content-Type
      if (method === 'POST') {
        xhr.setRequestHeader('Content-Type', 'application/json')
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const res: IHttpResponse<T> = JSON.parse(xhr.responseText)
              if (res.code !== 0 && res.code !== '0000') {
                console.error('HTTP请求失败:', res)
                reject(res)
                return
              }
              resolve(res.data)
            } catch (error) {
              console.error('解析响应数据失败:', error)
              reject(new Error('响应数据格式错误'))
            }
          } else {
            console.error('HTTP请求错误:', xhr.status, xhr.responseText)
            reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`))
          }
        }
      }

      // 发送请求
      if (method === 'POST' && data) {
        const postData = useHeader ? { ...data } : { ...data, token }
        xhr.send(JSON.stringify(postData))
      } else {
        xhr.send()
      }
    })
  })
}

/**
 * GET 请求的便捷方法
 * @param {string} url 请求地址
 * @param {Record<string, any>} params 查询参数（可选）
 * @param {string} baseUrl 基础地址（可选）
 * @return {Promise<T>} 返回 Promise 包装的响应数据
 */
function get<T = any>(url: string, params?: Record<string, any>, baseUrl?: string): Promise<T> {
  return httpRequest<T>({ url, method: 'GET', params, baseUrl })
}

/**
 * POST 请求的便捷方法
 * @param {string} url 请求地址
 * @param {any} data 请求数据
 * @param {string} baseUrl 基础地址（可选）
 * @return {Promise<T>} 返回 Promise 包装的响应数据
 */
function post<T = any>(url: string, data?: any, baseUrl?: string): Promise<T> {
  return httpRequest<T>({ url, method: 'POST', data, baseUrl })
}

export const http = {
  get,
  post
}
